import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, TIER_LABELS, type Tier } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/toast";
import { BrandHeading } from "@/components/BrandFrame";

const TIERS: Tier[] = ["license", "wholesale", "general"];

export default function AdminPercentagesPage() {
  const qc = useQueryClient();
  const { push } = useToast();
  const defaultsQ = useQuery({ queryKey: ["tier-defaults"], queryFn: api.tierDefaults });
  const [drafts, setDrafts] = useState<Record<Tier, string>>({ license: "", wholesale: "", general: "" });

  const setDef = useMutation({
    mutationFn: ({ tier, v }: { tier: Tier; v: number | null }) => api.setTierDefault(tier, v),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tier-defaults"] }); push({ title: "บันทึกค่าเริ่มต้นแล้ว" }); },
    onError: (e: any) => push({ title: "บันทึกไม่สำเร็จ", description: e.message, variant: "error" }),
  });
  const apply = useMutation({
    mutationFn: (tier: Tier) => api.applyTierDefault(tier),
    onSuccess: (r, tier) => push({ title: `ปรับใช้แล้ว ${r.changes.toLocaleString()} รายการของ ${TIER_LABELS[tier]}` }),
    onError: (e: any) => push({ title: "ปรับใช้ไม่สำเร็จ", description: e.message, variant: "error" }),
  });

  return (
    <div className="space-y-4">
      <BrandHeading>ตั้งค่าส่วนลดเริ่มต้น</BrandHeading>
      <p className="max-w-2xl text-sm text-muted-foreground">
        ไฟล์ xlsx ต้นทางมีส่วนลดต่อ SKU อยู่แล้ว · ตั้งค่าเริ่มต้นรายระดับเพื่อเขียนทับทุก SKU
        ที่ไม่ใช่ราคา NET ด้วยส่วนลดเดียวกัน · ราคา NET จะไม่ถูกเปลี่ยน · กรอกค่าแล้วต้องกด
        <strong> ปรับใช้</strong> ระบบถึงจะอัปเดตจริง
      </p>

      <div className="grid gap-4 md:grid-cols-3">
        {TIERS.map((tier) => {
          const cur = defaultsQ.data?.defaults.find((d) => d.tier === tier);
          const draft = drafts[tier];
          return (
            <Card key={tier} className="overflow-hidden">
              <div className="h-1 w-full bg-accent-orange" />
              <CardHeader>
                <Badge variant={tier}>{TIER_LABELS[tier]}</Badge>
                <CardTitle className="mt-1.5">{TIER_LABELS[tier]}</CardTitle>
                <CardDescription>
                  ค่าเริ่มต้นปัจจุบัน: {cur?.discount_percent != null ? `${(cur.discount_percent * 100).toFixed(1)}%` : "— ใช้ค่าต่อแถวเท่านั้น"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="เช่น 0.30 สำหรับ 30%"
                    value={draft}
                    inputMode="decimal"
                    onChange={(e) => setDrafts((d) => ({ ...d, [tier]: e.target.value }))}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const v = draft === "" ? null : Number(draft);
                      if (v != null && (Number.isNaN(v) || v < 0 || v > 1)) return push({ title: "ใส่ค่าระหว่าง 0 ถึง 1", variant: "error" });
                      setDef.mutate({ tier, v });
                    }}
                  >
                    บันทึก
                  </Button>
                </div>
                <Button
                  variant="primary"
                  className="w-full"
                  onClick={() => apply.mutate(tier)}
                  disabled={cur?.discount_percent == null}
                >
                  ปรับใช้กับทุก SKU
                </Button>
                <p className="text-[11px] text-muted-foreground">
                  คำนวณใหม่ <code>{tier}_unit_price = round(sale_price · (1 − default), 2)</code>
                  ให้ทุก SKU ที่ไม่ใช่ NET และไม่ถูกลบ · การแก้รายแถวภายหลังจะคงอยู่
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
